/* eslint-disable no-magic-numbers,i18n-text/no-en */
import { useMemo, useEffect, useState, useRef } from 'react';
import { FirstChapter } from './FirstChapter';
import { PreviewNotFirstChapter } from './PreviewNotFirstChapter';
import { useWindowResize } from '../../../../../utils/hooks/useWindowResize';
import { isLength } from '../../../../../../../../../lib/util/helpers';
import { ChapterState, DestinationVariants } from '../../../../../../../../../lib/models/TemplateTypes';
import { sendDataToAnalytics } from '../../../../../utils/sendDataToAnalytics';
import { setNextFlow } from '../../../hooks/setNextFlow';
import styles from './styles.module.scss';

const FILL_ANIMATION_SESSION_KEY = 'previewChapterFillAnimationPlayed';

// eslint-disable-next-line max-lines-per-function
export function PreviewChapterLayout({
  colors,
  fonts,
  pagesFlow,
  setPagesFlow,
  style,
  media,
  chapterIconMedia,
  bulletIcons,
  setPageHistory,
  showChapterIds,
  imageSelectionFlow,
  currentElements,
  // showCompletedChapters,
  setShowCompletedChapters,
  audioGuideModal,
  isAutoGuide,
}) {
  const { allPages, allChapters, currentChapterId } = pagesFlow;

  const allChaptersWithoutCompletedChapters = allChapters.filter(item => !item.content.completedChaptersButtons);
  const completedChapters = allChapters.filter(item => item.content.completedChaptersButtons);

  const otherChapters = useMemo(
    () => (isLength(allChaptersWithoutCompletedChapters) ? allChaptersWithoutCompletedChapters.slice(1) : []),
    [allChaptersWithoutCompletedChapters]
  );

  const { windowWidth } = useWindowResize();
  const { rootStyle = {} } = style;

  const showOtherChapters = otherChapters.filter(chap => showChapterIds.includes(chap.id));

  const isOddChapters = useMemo(() => otherChapters.length % 2 !== 0 && windowWidth < 1200, [otherChapters, windowWidth]);
  const isCompletedChapters = useMemo(() => completedChapters.some(el => el.id === currentChapterId), [completedChapters, currentChapterId]);

  const [chaptersAnimation, setChaptersAnimation] = useState({});
  const [isFillAnimationPlayed, setIsFillAnimationPlayed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(FILL_ANIMATION_SESSION_KEY) === 'true';
  });

  const hasAnimationStartedRef = useRef(false);

  const firstChapterToAnimate = allChaptersWithoutCompletedChapters[0];

  const firstChapter = allChaptersWithoutCompletedChapters[0];
  const firstChapterId = firstChapter?.id;

  const chaptersForAnimation = (firstChapterId ? [firstChapter] : []).concat(showOtherChapters);
  const chaptersIdsKey = chaptersForAnimation.map(ch => ch.id).join('-');

  useEffect(() => {
    if (chaptersForAnimation.length === 0 || isFillAnimationPlayed || (isAutoGuide && audioGuideModal)) {
      return;
    }

    hasAnimationStartedRef.current = true;

    const BORDER_DURATION = 2000;
    const IMAGE_DURATION = 3000;

    const timers = [];

    for (const [index, chapter] of chaptersForAnimation.entries()) {
      const borderDelay = index * BORDER_DURATION;
      const imageDelay = (index + 1) * BORDER_DURATION;

      timers.push(
        window.setTimeout(() => {
          setChaptersAnimation(prev => ({
            ...prev,
            [chapter.id]: 'border',
          }));
        }, borderDelay),
        window.setTimeout(() => {
          setChaptersAnimation(prev => ({
            ...prev,
            [chapter.id]: 'image',
          }));
        }, imageDelay)
      );
    }

    const totalDuration = chaptersForAnimation.length * BORDER_DURATION + IMAGE_DURATION;

    timers.push(
      window.setTimeout(() => {
        setChaptersAnimation({});
        setIsFillAnimationPlayed(true);

        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(FILL_ANIMATION_SESSION_KEY, 'true');
        }
      }, totalDuration + 50)
    );

    // eslint-disable-next-line consistent-return
    return () => {
      for (const id of timers) window.clearTimeout(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaptersIdsKey, chaptersForAnimation.length, isFillAnimationPlayed, audioGuideModal, isAutoGuide]);

  useEffect(() => {
    return () => {
      if (hasAnimationStartedRef.current && typeof window !== 'undefined') {
        window.sessionStorage.setItem(FILL_ANIMATION_SESSION_KEY, 'true');
      }
    };
  }, []);

  useEffect(() => {
    if (isCompletedChapters) {
      setShowCompletedChapters(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedChapters]);

  const chapterOnClick = (chapterId, flowObj, rules, stateObj, pageUid, chapterIndex) => {
    sendDataToAnalytics('Open chapter', chapterIndex);

    if ([ChapterState.COMPLETED, ChapterState.OPENED].includes(stateObj)) {
      const nextPage =
        flowObj?.goTo === DestinationVariants.NEXT
          ? allPages.find(page => page.id === pageUid)
          : allPages.find(page => page.elements.find(el => el.id === flowObj?.goTo));

      setNextFlow(flowObj, rules, nextPage, chapterId, pagesFlow, setPagesFlow, setPageHistory, currentElements);
    }
  };

  return (
    <section
      style={{
        display: 'flex',
        flexShrink: 0,
        flexGrow: 1,
        backgroundColor: colors.backgroundColorSecond,
        width: '100%',
      }}
    >
      <div className={styles.chapterContainer} style={{ ...rootStyle }}>
        <FirstChapter
          colors={colors}
          fonts={fonts}
          pagesFlow={pagesFlow}
          setPagesFlow={setPagesFlow}
          style={style}
          media={media}
          chapterIconMedia={chapterIconMedia}
          bulletIcons={bulletIcons}
          chapterOnClick={chapterOnClick}
          setPageHistory={setPageHistory}
          imageSelectionFlow={imageSelectionFlow}
          currentElements={currentElements}
          animationPhase={chaptersAnimation[firstChapterToAnimate?.id]}
        />

        <div className={styles.notFirstChaptersContainer}>
          {showOtherChapters.map((chapter, index) => (
            <PreviewNotFirstChapter
              key={chapter.id}
              chapter={chapter}
              chaptersLength={showOtherChapters.length}
              pagesFlow={pagesFlow}
              style={style}
              colors={colors}
              fonts={fonts}
              mediaOptions={media}
              chapterIconMedia={chapterIconMedia}
              chapterOnClick={chapterOnClick}
              chapterIndex={index + 2}
              imageSelectionFlow={imageSelectionFlow}
              animationPhase={chaptersAnimation[chapter.id]}
            />
          ))}
          {isOddChapters && <div style={{ flex: '1 1 33.33%' }} />}
        </div>
      </div>
    </section>
  );
}
